import {
  getCookie,
  getCurrentZipcodeCookie,
  setCookie
} from "@directv-digital/dds.services.cookie-service";
import ReportingService from "src/services/ReportingService";
import { getCurrentBreakpoint } from "src/utils/Breakpoint/Breakpoint";
import { ImpressionsData } from "lib/helper/build-impressions-data";

interface ILoadCookiesArgs {
  reqQueryParams: Record<string, string>;
  cookieList?: any[];
}

export const loadCookies = (args: ILoadCookiesArgs) => {
  const { reqQueryParams, cookieList } = args;

  // WAT-10896: operation==='write' entries are now set server-side in
  // handleAppRouter via res.cookie() before app.render(), so writing them
  // again client-side would duplicate work and violate the no-collision AC.
  // Non-write entries (e.g. utCode lookups) still flow through client logic.
  const oemCookie = cookieList?.find?.(
    cookie => cookie?.type === "oem" && cookie?.operation !== "write"
  );
  if (oemCookie) {
    const name = oemCookie?.name;
    const value = oemCookie?.value;
    const domain = oemCookie?.options?.domain;
    if (
      ![name, value, domain].includes(undefined) ||
      ![name, value, domain].includes(null)
    ) {
      setCookie(name, value, "/", 30, false, domain);
    }
  }

  const rpid = cookieList?.find?.(
    cookie => cookie?.type === "rpid" && cookie?.operation !== "write"
  );
  if (rpid?.name && rpid?.value) {
    setCookie(rpid.name, rpid.value, "/", 30, false);
  }

  const utCode = cookieList?.find?.(cookie => cookie?.name === "utCode");
  if (utCode) {
    setCookie(utCode.name, utCode.value, "/", 0, true);
  }

  if (reqQueryParams?.agentId) {
    setCookie(
      "agentId",
      reqQueryParams.agentId,
      "/",
      1,
      true,
      window.location.hostname
    );
  } else {
    document.cookie =
      "agentId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  }
};

export const handleMissingZipRsn = async (ufContext: any) => {
  // WAT-11617: parity with the Pages Router (src/hooks/useRsnFeeHandler.tsx).
  // This previously read getCookie("zipcode") — a cookie nothing sets (the app's
  // cookie is "currentZipcode") — with no fallback, so the context zip stayed
  // empty on App Router pages and the zipcode module never prefilled its
  // ZIP/county inputs. getCurrentZipcodeCookie() resolves
  // currentZipcode → x-dtv-edgescape → "75001" default, exactly like Pages.
  const zip = getCurrentZipcodeCookie();
  ufContext?.globalZipCode?.update?.(zip, true);
};

interface ITrackImpressionsArgs extends Partial<ImpressionsData> {
  page: string;
  pagePath: string;
  root: string;
  reqQueryParams: Record<string, string>;
}

// WAT-10897: sendImpressions (fired from trackImpressions) always fires a manual
// firePageLoadEvent, so DMI's automatic page-load must be suppressed or every route
// double-fires. Call this SYNCHRONOUSLY at effect mount — the same point and order
// the Pages Router uses (pages/index.tsx:221, pages/tools/signal-strength:294,
// pages/tools/outdoor-signal-strength:235). Doing it here rather than inside the
// requestIdleCallback send() closes the race window: if DMI's auto page-load fires
// between mount and the idle callback, a suppression deferred to idle lands too late.
// Guarded only on DMI existence (GTM-injected, may not be present yet), matching Pages.
export const suppressAutoPageLoad = () => {
  if (window.DataMappingInterface?.disableAutoPageLoad) {
    window.DataMappingInterface.disableAutoPageLoad();
  }
};

export const trackImpressions = (props: ITrackImpressionsArgs) => {
  const { page, pagePath, root, reqQueryParams, ...impressionsData } = props;
  const send = () => {
    const service = new ReportingService();
    service.sendImpressions(
      {
        // Guard data/feed explicitly even after the spread — an explicit
        // `undefined` here would otherwise defeat the {} defaults and hit
        // sendImpressions's early-return, silently reintroducing WAT-10897.
        ...impressionsData,
        data: impressionsData.data || [],
        feed: impressionsData.feed || {}
      },
      getCurrentBreakpoint(),
      page,
      pagePath,
      root,
      reqQueryParams
    );
  };
  if ("requestIdleCallback" in window) {
    requestIdleCallback(send, { timeout: 2000 });
  } else {
    setTimeout(send, 0);
  }
};

interface IPostPageLoadEffectsArgs {
  evolvUUID: string;
}

export const postPageLoadEffects = (props: IPostPageLoadEffectsArgs) => {
  if (getCookie("userVisitCount")) {
    const newCount = parseInt(getCookie("userVisitCount")) + 1;
    setCookie(
      "userVisitCount",
      newCount.toString(),
      "/",
      180,
      false,
      ".directv.com"
    );
  } else {
    setCookie("userVisitCount", "2", "/", 180, false, ".directv.com");
  }
  const evolvUUID = window?.sessionStorage?.getItem("evolvUUID");
  if (evolvUUID !== props.evolvUUID && props.evolvUUID) {
    window?.sessionStorage?.setItem("evolvUUID", props.evolvUUID);
    setCookie("evolvUUID", props.evolvUUID, "/", 180, true);
  }
  const headings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");
  headings.forEach(heading => heading?.removeAttribute("aria-label"));
};
